/**
 * 营销落地页区块注册表。
 * `ssrSync: true` 的区块同步 import，保证 SSR HTML 含首屏文案。
 */
import MarketingLandingHero from './MarketingLandingHero.vue'
import MarketingLandingHeader from './MarketingLandingHeader.vue'
import MarketingLandingSocialProof from './MarketingLandingSocialProof.vue'
import MarketingLandingFaq from './MarketingLandingFaq.vue'

export const MARKETING_LANDING_SSR_SECTIONS = {
  hero: MarketingLandingHero,
  header: MarketingLandingHeader,
  socialProof: MarketingLandingSocialProof,
  faq: MarketingLandingFaq,
}

/** 无 Hero 媒体时的纯文案首屏（SEO 子页等） */
export const MARKETING_LANDING_HEADER_SECTION = {
  id: 'header',
  ssrSync: true,
}

export const MARKETING_LANDING_SECTIONS = [
  { id: 'hero', ssrSync: true },
  { id: 'socialProof', ssrSync: true, minHeight: '15rem' },
  {
    id: 'bento',
    minHeight: '48rem',
    loader: () => import('./MarketingLandingBento.vue'),
  },
  {
    id: 'why',
    minHeight: '24rem',
    loader: () => import('./MarketingLandingWhy.vue'),
  },
  {
    id: 'howItWorks',
    minHeight: '36rem',
    loader: () => import('./MarketingLandingHowItWorks.vue'),
  },
  {
    id: 'features',
    minHeight: '90rem',
    loader: () => import('./MarketingLandingFeatures.vue'),
  },
  {
    id: 'testimonials',
    minHeight: '20rem',
    loader: () => import('./MarketingLandingTestimonials.vue'),
  },
  { id: 'faq', ssrSync: true, minHeight: '18rem' },
  {
    id: 'bottomCta',
    minHeight: '16rem',
    loader: () => import('./MarketingLandingBottomCta.vue'),
  },
]

/** 首页：FAQ 改异步挂载，避免 FAQ 标题参与 LCP、减少首屏 HTML 解析量 */
export const HOME_INDEX_MARKETING_SECTIONS = [
  { id: 'hero', ssrSync: true },
  { id: 'socialProof', ssrSync: true, minHeight: '15rem' },
  {
    id: 'bento',
    minHeight: '48rem',
    loader: () => import('./MarketingLandingBento.vue'),
  },
  {
    id: 'why',
    minHeight: '24rem',
    loader: () => import('./MarketingLandingWhy.vue'),
  },
  {
    id: 'howItWorks',
    minHeight: '36rem',
    loader: () => import('./MarketingLandingHowItWorks.vue'),
  },
  {
    id: 'features',
    minHeight: '90rem',
    loader: () => import('./MarketingLandingFeatures.vue'),
  },
  {
    id: 'testimonials',
    minHeight: '20rem',
    loader: () => import('./MarketingLandingTestimonials.vue'),
  },
  { id: 'faq', minHeight: '18rem', loader: () => import('./MarketingLandingFaq.vue') },
  {
    id: 'discoverTools',
    minHeight: '48rem',
    loader: () => import('./MarketingDiscoverTools.vue'),
  },
  {
    id: 'bottomCta',
    minHeight: '16rem',
    loader: () => import('./MarketingLandingBottomCta.vue'),
  },
]

/** AI Animate Face：与首页相同，但不展示 Testimonials / Features */
export const FACE_ANIMATE_PAGE_MARKETING_SECTIONS = HOME_INDEX_MARKETING_SECTIONS.filter(
  (section) => section.id !== 'testimonials' && section.id !== 'features',
)

/** Talking Photo / Talking Animals：Hero + below-fold 异步占位，FAQ 异步避免参与 LCP */
export const TALKING_PAGE_MARKETING_SECTIONS = [
  { id: 'hero', ssrSync: true },
  { id: 'socialProof', ssrSync: true, minHeight: '15rem' },
  {
    id: 'bento',
    minHeight: '48rem',
    loader: () => import('./MarketingLandingBento.vue'),
  },
  {
    id: 'why',
    minHeight: '24rem',
    loader: () => import('./MarketingLandingWhy.vue'),
  },
  {
    id: 'howItWorks',
    minHeight: '36rem',
    loader: () => import('./MarketingLandingHowItWorks.vue'),
  },
  {
    id: 'features',
    minHeight: '90rem',
    loader: () => import('./MarketingLandingFeatures.vue'),
  },
  {
    id: 'testimonials',
    minHeight: '20rem',
    loader: () => import('./MarketingLandingTestimonials.vue'),
  },
  { id: 'faq', minHeight: '18rem', loader: () => import('./MarketingLandingFaq.vue') },
  {
    id: 'discoverTools',
    minHeight: '28rem',
    loader: () => import('./MarketingDiscoverTools.vue'),
  },
  {
    id: 'bottomCta',
    minHeight: '16rem',
    loader: () => import('./MarketingLandingBottomCta.vue'),
  },
]

/** Audio Cleaner：用 Use Cases 列表替换 Bento */
export const AUDIO_CLEANER_PAGE_MARKETING_SECTIONS = [
  { id: 'hero', ssrSync: true },
  { id: 'socialProof', ssrSync: true, minHeight: '15rem' },
  {
    id: 'useCases',
    minHeight: '36rem',
    loader: () => import('./MarketingLandingUseCases.vue'),
  },
  {
    id: 'why',
    minHeight: '24rem',
    loader: () => import('./MarketingLandingWhy.vue'),
  },
  {
    id: 'howItWorks',
    minHeight: '36rem',
    loader: () => import('./MarketingLandingHowItWorks.vue'),
  },
  {
    id: 'features',
    minHeight: '90rem',
    loader: () => import('./MarketingLandingFeatures.vue'),
  },
  {
    id: 'testimonials',
    minHeight: '20rem',
    loader: () => import('./MarketingLandingTestimonials.vue'),
  },
  { id: 'faq', minHeight: '18rem', loader: () => import('./MarketingLandingFaq.vue') },
  {
    id: 'discoverTools',
    minHeight: '28rem',
    loader: () => import('./MarketingDiscoverTools.vue'),
  },
  {
    id: 'bottomCta',
    minHeight: '16rem',
    loader: () => import('./MarketingLandingBottomCta.vue'),
  },
]

const MARKETING_LANDING_SECTION_BY_ID = Object.fromEntries(
  [
    MARKETING_LANDING_HEADER_SECTION,
    ...MARKETING_LANDING_SECTIONS,
    ...TALKING_PAGE_MARKETING_SECTIONS,
    ...AUDIO_CLEANER_PAGE_MARKETING_SECTIONS,
  ].map((section) => [section.id, section]),
)

/** Talking Photo (/talking-photo-ai) 页营销区块 */
export const TALKING_PHOTO_MARKETING_SECTION_IDS = [
  'header',
  'howItWorks',
  'why',
  'faq',
  'bottomCta',
]

/** Talking Animals (/talking-animals) 页营销区块 */
export const TALKING_ANIMALS_MARKETING_SECTION_IDS = [
  'header',
  'howItWorks',
  'why',
  'faq',
  'bottomCta',
]

/** AI Animate Face 页营销区块（与 index 相同：单 MarketingLanding + flex order 控制视觉顺序） */
export const FACE_ANIMATE_MARKETING_SECTION_IDS = [
  'header',
  'howItWorks',
  'why',
  'faq',
  'bottomCta',
]

/** @param {string[]} ids 按传入顺序解析区块配置 */
export function marketingLandingSectionsForIds(ids) {
  if (!Array.isArray(ids)) return []
  return ids
    .map((id) => MARKETING_LANDING_SECTION_BY_ID[id])
    .filter(Boolean)
}
